import * as React from 'react'
import { Link } from 'react-router-dom'
import { ChevronRight, Layers, Star } from 'lucide-react'

import { Badge } from '@/components/ui/badge'
import { formatDate } from '@/lib/utils'
import type { GenerationJob, JobStatus } from '@/types'

interface Props {
  assemblyId: number
  scenes: GenerationJob[]
  stitched: GenerationJob | null
  onStar: (jobId: number, currentlyStarred: boolean) => Promise<void>
}

type AssemblyStage = 'queued' | 'processing' | 'completed' | 'failed' | 'cancelled'

function deriveAssemblyStage(scenes: GenerationJob[], stitched: GenerationJob | null): AssemblyStage {
  if (stitched) return stitched.status as AssemblyStage
  if (scenes.some((s) => s.status === 'failed')) return 'failed'
  if (scenes.some((s) => s.status === 'processing')) return 'processing'
  if (scenes.some((s) => s.status === 'queued')) return 'queued'
  if (scenes.length > 0 && scenes.every((s) => s.status === 'completed')) return 'completed'
  if (scenes.length > 0 && scenes.every((s) => s.status === 'cancelled')) return 'cancelled'
  return 'queued'
}

function stageBadge(stage: AssemblyStage): {
  label: string
  variant: 'default' | 'secondary' | 'destructive' | 'outline'
  className?: string
} {
  switch (stage) {
    case 'completed':
      return { label: 'Complete', variant: 'default', className: 'bg-green-500/10 text-green-600 border-green-500/30' }
    case 'failed':
      return { label: 'Failed', variant: 'destructive' }
    case 'processing':
      return { label: 'Generating', variant: 'secondary', className: 'animate-pulse' }
    case 'queued':
      return { label: 'Queued', variant: 'secondary' }
    case 'cancelled':
      return { label: 'Cancelled', variant: 'outline' }
    default:
      return { label: stage, variant: 'outline' }
  }
}

function chipColor(status: JobStatus): string {
  switch (status) {
    case 'completed':
      return 'bg-green-500/10 text-green-600 border-green-500/30 hover:bg-green-500/20'
    case 'processing':
      return 'bg-orange-400/10 text-orange-500 border-orange-400/30 hover:bg-orange-400/20 animate-pulse'
    case 'failed':
      return 'bg-red-500/10 text-red-500 border-red-500/30 hover:bg-red-500/20'
    case 'cancelled':
      return 'bg-zinc-500/10 text-zinc-500 border-zinc-500/30 hover:bg-zinc-500/20'
    case 'queued':
    default:
      return 'bg-red-500/10 text-red-500 border-red-500/30 hover:bg-red-500/20'
  }
}

export function AssemblyRow({ assemblyId, scenes, stitched, onStar }: Props) {
  const headline = stitched ?? scenes[0] ?? null
  const stage = deriveAssemblyStage(scenes, stitched)
  const badge = stageBadge(stage)

  const starTarget = stitched ?? scenes[0] ?? null
  const [starred, setStarred] = React.useState(starTarget?.is_starred ?? false)
  const [starPending, setStarPending] = React.useState(false)

  React.useEffect(() => {
    setStarred(starTarget?.is_starred ?? false)
  }, [starTarget?.is_starred])

  const handleStar = async (e: React.MouseEvent) => {
    e.preventDefault()
    e.stopPropagation()
    if (!starTarget || starPending) return
    const prev = starred
    setStarred(!prev)
    setStarPending(true)
    try {
      await onStar(starTarget.id, prev)
    } catch {
      setStarred(prev)
    } finally {
      setStarPending(false)
    }
  }

  if (!headline) return null

  // The row's link target is the stitched job's detail page if available, otherwise the first scene.
  const linkTarget = stitched ?? scenes[0]
  const linkId = linkTarget?.id ?? 0

  const totalDuration = scenes.reduce((sum, s) => sum + (s.duration_seconds || 0), 0)

  return (
    <Link
      to={`/created-assets/${linkId}`}
      className="flex items-center gap-3 px-4 py-3 border-b border-border hover:bg-muted/50 transition-colors"
    >
      <span className="text-xs text-muted-foreground w-14 shrink-0 inline-flex items-center gap-1">
        <Layers className="h-3 w-3" />
        A{assemblyId}
      </span>
      <span className="flex-1 text-sm truncate min-w-0">{headline.prompt_text}</span>

      {/* Scene tabs — clickable chips, one per scene */}
      <div className="hidden md:flex items-center gap-1 shrink-0">
        {scenes.map((scene) => (
          <Link
            key={scene.id}
            to={`/created-assets/${scene.id}`}
            onClick={(e) => e.stopPropagation()}
            className={`inline-flex items-center px-1.5 py-0.5 rounded border text-[10px] transition-colors ${chipColor(scene.status)}`}
            title={`Scene ${scene.scene_number ?? '?'} · ${scene.status}`}
          >
            S{scene.scene_number ?? '?'}
          </Link>
        ))}
      </div>

      <Badge
        variant={badge.variant}
        className={`shrink-0 text-[10px] ${badge.className ?? ''}`}
      >
        {badge.label}
      </Badge>
      <span className="text-xs text-muted-foreground w-10 shrink-0 hidden md:block">
        {headline.aspect_ratio}
      </span>
      <span className="text-xs text-muted-foreground w-10 shrink-0 hidden md:block">
        {totalDuration}s
      </span>
      <span className="text-xs text-muted-foreground w-16 shrink-0 hidden lg:block">
        {formatDate(headline.created_at)}
      </span>
      <button
        type="button"
        onClick={handleStar}
        disabled={starPending}
        aria-label={starred ? 'Unstar assembly' : 'Star assembly'}
        className={`shrink-0 p-0.5 rounded transition-colors ${starred ? 'text-yellow-400' : 'text-muted-foreground hover:text-yellow-400'}`}
      >
        <Star className="h-3.5 w-3.5" fill={starred ? 'currentColor' : 'none'} />
      </button>
      <ChevronRight className="h-4 w-4 text-muted-foreground shrink-0" />
    </Link>
  )
}
